import axios from 'axios';

const API_DOMAIN = process.env.VUE_APP_API_URL;

// Constants for common error messages
const ERROR_MESSAGES = {
    GENERAL: 'Oops, something went wrong. Please try again later.',
    ACCESS_DENIED: {
        title: 'Looks like you\'re trying to access Career Connect',
        message: 'You are not yet provisioned to use Career Connect. Click below to learn more about how to get access.',
        image: 'zoeConcerned',
        primaryButton: {
            text: 'Learn More',
            link: 'https://careerconnect.discoveryeducation.com/',
        },
    },
};

// Helper function to handle common error cases
const handleError = ({ commit, dispatch }, errorMessage) => {
    commit('UPDATE_ERROR', errorMessage);
    dispatch('updateApp', { active: true });
    return null;
};

/**
 * Check if the user has access to Career Connect via product settings (CARCON_ENABLED)
 * @param {string} accessToken - The user's access token
 * @returns {Promise<boolean>} - True if the user has access, false otherwise
 */
const checkAccessViaProductSettings = async (accessToken) => {
    try {
        const response = await axios.get(`${API_DOMAIN}/deproxy/v1/users/product_settings`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });

        return response.data.settings.some(
            (setting) => setting.code === 'CARCON_ENABLED' && setting.value === true,
        );
    } catch (error) {
        return false;
    }
};

const getDEUser = async ({ commit, dispatch }, data) => {
    const accessToken = data.token;
    let response;

    try {
        response = await axios.get(`${API_DOMAIN}/deproxy/v1/users/me?return_avatar=true`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        commit('UPDATE_ERROR', null);
    } catch {
        return handleError({ commit, dispatch }, ERROR_MESSAGES.GENERAL);
    }

    const {
        id,
        first_name: firstName,
        last_name: lastName,
        site,
        email,
        products,
        timezone,
        avatar: avatarUrl,
        roles,
    } = response.data.user;

    // Check for student role first - early return if student
    const isStudent = roles && roles.some((role) => role.code === 'STUDENT');
    if (isStudent) {
        return handleError({ commit, dispatch }, ERROR_MESSAGES.GENERAL);
    }

    // Check for access via product code (from /me endpoint)
    const hasAccessViaProduct = !!products.find((product) => product.code === 'CARCON');

    // If no product access, check settings (from /product_settings endpoint)
    if (!hasAccessViaProduct) {
        const hasAccessViaSettings = await checkAccessViaProductSettings(accessToken);

        // No access via either method
        if (!hasAccessViaSettings) {
            return handleError({ commit, dispatch }, ERROR_MESSAGES.ACCESS_DENIED);
        }
    }

    return commit('UPDATE_USER_DATA', {
        id,
        firstName,
        lastName,
        site,
        email,
        timezone,
        avatarUrl,
        roles,
    });
};

const getSite = async ({ commit }, payload) => {
    const { siteId, token } = payload;
    let site;
    try {
        const response = await axios.get(`${API_DOMAIN}/deproxy/v1/sites/${siteId}/location`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        site = response.data.site;
    } catch (e) {
        console.log(e);
    }

    if (site) {
        return commit('UPDATE_USER_DATA', {
            site,
        });
    }
    return null;
};

export default { getDEUser, getSite };
